import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, FileInput, FileUp, PencilRuler, Sparkles, TriangleAlert, XCircle } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";

import { apiUpload } from "../../api/client";
import { Badge, Banner, Button, Card, DataTable, Field, Input, PageHeader, Stat } from "../../ds";

interface ImportItem {
  page: string;
  visual: string;
  powerbiType: string;
  widgetType: string | null;
  status: "converted" | "approximated" | "unsupported" | "skipped";
  notes?: string | string[] | null;
}
interface ImportResult {
  summary: { pages: number; visuals: number; converted: number; approximated: number; unsupported: number; skipped?: number };
  items: ImportItem[];
  measures: Array<{ name: string; dax: string; suggestion: unknown }>;
  tables: Array<{ name: string; inferred?: boolean; datasetId?: string | null; columns: Array<{ name: string; dataType?: string }> }>;
  relationships?: Array<{ fromTable: string; fromColumn: string; toTable: string; toColumn: string; crossFilterDirection: string; isActive: boolean }>;
  warnings: string[];
}
interface ImportResponse {
  reportId?: string;
  result: ImportResult;
}

const STATUS_TONE = { converted: "success", approximated: "warning", unsupported: "danger", skipped: "neutral" } as const;
const ACCEPT = [".pbix", ".pbit", ".zip"];

export function PowerBiImportPage() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (f: File | null | undefined) => {
    setError(null);
    setPreview(null);
    if (!f) return;
    if (!ACCEPT.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      setError("Choose a Power BI file: .pbit (template), .pbix, or a .zip of a PBIP project folder.");
      return;
    }
    setFile(f);
    setName(f.name.replace(/\.(pbix|pbit|zip)$/i, ""));
    analyze.mutate(f);
  };

  const send = (f: File, create: boolean) => {
    const form = new FormData();
    form.append("file", f);
    form.append("create", String(create));
    if (name.trim()) form.append("name", name.trim());
    return apiUpload<ImportResponse>("/api/v1/admin/powerbi/import", form);
  };

  const analyze = useMutation({
    mutationFn: (f: File) => send(f, false),
    onSuccess: (r) => setPreview(r.result),
    onError: (e) => setError(e instanceof Error ? e.message : "The file couldn't be read."),
  });
  const create = useMutation({
    mutationFn: () => send(file as File, true),
    onSuccess: (r) => (r.reportId ? navigate(`/admin/reports/${r.reportId}/edit`) : setError("Import finished but no report id was returned.")),
    onError: (e) => setError(e instanceof Error ? e.message : "The import failed."),
  });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    pick(e.dataTransfer.files?.[0]);
  };

  const s = preview?.summary;
  const needs = preview?.measures.filter((m) => !m.suggestion) ?? [];

  return (
    <div className="ds-page">
      <PageHeader
        eyebrow="Build faster"
        title="Import from Power BI"
        subtitle="Turn an existing Power BI report into a draft here: pages, visuals, positions and fields come across automatically. You (or an AI assistant) then connect the data and fine-tune."
      />

      <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", alignItems: "start" }}>
        <div className="ds-stack">
          <Card>
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              onClick={() => input.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current?.click()}
              style={{
                border: `2px dashed ${drag ? "var(--accent)" : "var(--border-strong)"}`,
                background: drag ? "var(--accent-soft)" : "var(--surface-2)",
                borderRadius: 12,
                padding: "44px 24px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 120ms ease",
              }}
            >
              <div className="ds-empty__icon" style={{ margin: "0 auto 12px" }}><FileUp size={24} /></div>
              <div style={{ fontWeight: 650, fontSize: 16 }}>{file ? file.name : "Drop a Power BI file here, or click to choose"}</div>
              <div className="ds-small ds-muted" style={{ marginTop: 6 }}>.pbit template (recommended) · .pbix · .zip of a PBIP project · up to 50 MB</div>
              <input ref={input} type="file" accept={ACCEPT.join(",")} hidden onChange={(e) => pick(e.target.files?.[0])} />
            </div>
            {analyze.isPending ? <div className="ds-small ds-muted" style={{ marginTop: 12 }}>Reading the report layout…</div> : null}
            {error ? <div style={{ marginTop: 12 }}><Banner tone="danger" title="Import problem">{error}</Banner></div> : null}
          </Card>

          {preview && s ? (
            <>
              <div className="ds-grid ds-grid--4">
                <Stat icon={FileInput} label="pages" value={s.pages} />
                <Stat icon={CheckCircle2} tone="success" label="visuals converted" value={s.converted} />
                <Stat icon={TriangleAlert} tone="warning" label="approximated" value={s.approximated} />
                <Stat icon={XCircle} tone="danger" label="not supported" value={s.unsupported} />
              </div>
              <Card title="Visual by visual" subtitle="What each Power BI visual becomes here" flush>
                <div style={{ height: 8 }} />
                <DataTable<ImportItem>
                  rows={preview.items}
                  rowKey={(i) => `${i.page}-${i.visual}`}
                  maxHeight={420}
                  columns={[
                    { key: "page", header: "Page", sortValue: (i) => i.page, cell: (i) => i.page },
                    { key: "visual", header: "Visual", cell: (i) => <strong>{i.visual}</strong> },
                    { key: "from", header: "Power BI", cell: (i) => <span className="ds-mono ds-muted">{i.powerbiType}</span> },
                    { key: "to", header: "Becomes", cell: (i) => (i.widgetType ? <span className="ds-mono">{i.widgetType}</span> : "—") },
                    { key: "st", header: "Result", sortValue: (i) => i.status, cell: (i) => <Badge tone={STATUS_TONE[i.status]} dot>{i.status}</Badge> },
                    { key: "notes", header: "Notes", cell: (i) => <span className="ds-small ds-muted">{Array.isArray(i.notes) ? i.notes.join(" · ") : i.notes ?? ""}</span> },
                  ]}
                />
              </Card>
            </>
          ) : null}
        </div>

        <div className="ds-stack">
          <Card title={preview ? "Create the draft" : "How it works"}>
            {preview ? (
              <div className="ds-stack">
                <Field label="Report name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                {needs.length ? (
                  <Banner tone="warning" title={`${needs.length} measure(s) need a human or AI touch`}>
                    Complex DAX can't be translated automatically. They're listed in the draft notes; an AI assistant can finish them through the platform connector.
                  </Banner>
                ) : null}
                <Banner tone={preview.tables.some(t => t.inferred) ? "warning" : "info"} title={preview.tables.some(t => t.inferred) ? "Original data links are unavailable" : `${preview.relationships?.length ?? 0} source relationships found`}>
                  {preview.tables.some(t => t.inferred)
                    ? "This file contains the report layout without a readable semantic model. Use the original model's PBIX/PBIT or a PBIP package with its SemanticModel folder to recover the calendar and table links."
                    : "Relationships are restored when both source tables and their key fields are mapped during import. This layout upload alone does not connect the data or verify the totals."}
                </Banner>
                <Banner tone="info" title="Next: connect the data">Visuals arrive with their fields named. In the designer, pick the dataset for each visual (or let an AI map them).</Banner>
                <Button variant="primary" size="lg" icon={PencilRuler} loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>
                  Create draft & open designer
                </Button>
              </div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
                <li>In Power BI Desktop choose <strong>File → Export → Power BI template</strong> (.pbit), or save as a <strong>PBIP</strong> project and zip the folder.</li>
                <li>Drop the file here — we show what converts before anything is created.</li>
                <li>Create the draft, connect datasets in the designer, then publish.</li>
              </ol>
            )}
          </Card>
          {preview && needs.length ? (
            <Card title="Measures to finish" flush>
              <div className="ds-list">
                {needs.slice(0, 12).map((m) => (
                  <div key={m.name} className="ds-list__row">
                    <div className="ds-list__main">
                      <div className="ds-list__title">{m.name}</div>
                      <div className="ds-mono ds-faint ds-truncate">{m.dax}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
          <Card>
            <div className="ds-row" style={{ alignItems: "flex-start" }}>
              <Sparkles size={18} color="var(--accent)" />
              <div className="ds-small">
                <strong>Faster with AI:</strong> after importing, ask Claude or Codex to “bind every visual in this draft to the matching dataset fields and translate the remaining measures”.
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
